import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";

const CompetencyPointDialog = ({ open, points = [], mapping, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState({
    id: null,
    title: "",
    description: "",
    type: "WHITE",
    weight: 0,
  });

  useEffect(() => {
    setForm({
      id: null,
      title: "",
      description: "",
      type: "WHITE",
      weight: 0,
    });
  }, [open]);

  const handleEdit = (p) => {
    setForm(p);
  };

  const handleSubmit = () => {
    onSave({
      ...form,
      compToJobId: mapping?.id,
    });

    setForm({
      id: null,
      title: "",
      description: "",
      type: "WHITE",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Competency Points</DialogTitle>

      <DialogContent dividers>
        {/* POINTS LIST */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: 300,
            overflowY: "auto",
            pr: 1,
          }}
        >
          {points.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No points added yet
            </Typography>
          )}

          {points.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",
              }}
            >
              {/* LEFT CONTENT */}
              <Box>
                <Typography fontWeight={600}>{p.title}</Typography>

                <Typography variant="body2" color="text.secondary">
                  {p.description}
                </Typography>

                <Box display="flex" gap={1} mt={0.5}>
                  <Typography variant="caption">Type: {p.type}</Typography>

                  <Typography variant="caption">Weight: {p.weight ?? 0}</Typography>
                </Box>
              </Box>

              {/* ACTIONS */}
              <Box display="flex" gap={1}>
                <IconButton size="small" onClick={() => handleEdit(p)}>
                  <EditIcon fontSize="small" />
                </IconButton>

                <IconButton size="small" onClick={() => onDelete(p.id)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>

        {/* DIVIDER */}
        <Divider sx={{ mb: 2 }} />

        {/* FORM SECTION */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            fullWidth
          />

          <TextField
            label="Weight"
            type="number"
            value={form.weight || ""}
            onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })}
            fullWidth
          />

          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            multiline
            minRows={2}
            sx={{ gridColumn: "1 / -1" }}
          />

          <TextField
            select
            label="Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            fullWidth
            SelectProps={{ native: true }}
          >
            <option value="WHITE">WHITE</option>
            <option value="BLACK">BLACK</option>
          </TextField>

          {/* ACTION BUTTON */}
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
            }}
          >
            <MDButton color="primary" variant="contained" onClick={handleSubmit}>
              {form.id ? "Update Point" : "Add Point"}
            </MDButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CompetencyPointDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  points: PropTypes.array,
  mapping: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CompetencyPointDialog;
